(function () {
  'use strict';

  var DEFAULT_MAX_AGE_MONTHS = 60;

  var AGE_CLUSTER_RANGES = {
    1: { min: 0, max: 3 },
    2: { min: 3, max: 6 },
    3: { min: 6, max: 12 },
    4: { min: 12, max: 24 },
    5: { min: 24, max: 60 },
  };

  var SKILL_ALIASES = {
    all: ['all'],
    cognitive: [
      'cognitive',
      'problem solving', 'problem-solving', 'problem_solving',
      'puzzle', 'puzzles',
      'logic', 'logical', 'thinking', 'concentration',
      'math', 'counting', 'count', 'abacus', 'numbers', 'number',
      'shape', 'shapes', 'sorting', 'sorter', 'sort',
      'matching', 'match',
      'object permanence', 'permanence',
      'cause and effect', 'cause & effect',
      'nesting', 'nest',
      'memory', 'discovery', 'discover',
    ],
    fine_motor: [
      'fine motor', 'fine-motor', 'fine_motor',
      'motor skill', 'motor skills',
      'grasp', 'grip', 'grab', 'pinch', 'pincer', 'holding', 'hold',
      'stack', 'stacker', 'stacking', 'nest', 'nesting',
      'peg', 'pegs',
      'rattle', 'busy beads',
      'beads', 'thread', 'threading',
    ],
    gross_motor: [
      'gross motor', 'gross-motor', 'gross_motor',
      'crawl', 'crawling',
      'walk', 'walker', 'walking',
      'standing', 'stand',
      'push', 'pull',
      'tummy time', 'tummy-time',
      'movement', 'wobble', 'wobbler', 'balance', 'balancing',
      'reach', 'rolling', 'roll',
    ],
    sensory: [
      'sensory', 'texture', 'textures', 'touch', 'feel', 'tactile',
      'teether', 'teething', 'teethers',
      'discovery', 'play kit', 'play mat',
      'cloth book', 'mirror',
    ],
    visual: [
      'visual', 'vision', 'sight', 'see', 'seeing',
      'tracking', 'track', 'focus',
      'high contrast', 'high-contrast', 'black and white', 'black & white',
      'flashcard', 'flash card', 'flash-card', 'flashcards',
      'mobile', 'spinning', 'spiral',
    ],
    auditory: [
      'auditory', 'sound', 'sounds', 'hearing',
      'music', 'musical', 'listening',
      'shaker', 'shakers', 'rhythm',
      'rattle', 'rattles', 'drum',
    ],
    language: [
      'language', 'speech', 'words', 'word',
      'vocabulary', 'vocab',
      'reading', 'read', 'reader',
      'book', 'books', 'board book', 'storybook',
      'story', 'stories',
      'spelling', 'phonics', 'alphabet', 'letters',
      'flashcard', 'flashcards',
    ],
  };

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[–—]/g, '-')
      .replace(/&/g, ' and ')
      .replace(/[_/]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function clampAge(months) {
    if (!Number.isFinite(months)) return null;
    return Math.max(0, Math.round(months));
  }

  function yearsToMonths(years) {
    return Number.parseFloat(years) * 12;
  }

  function normalizeRange(min, max) {
    var safeMin = clampAge(min);
    var safeMax = clampAge(max);

    if (safeMin === null || safeMax === null) return null;
    if (safeMin > safeMax) {
      return { min: safeMax, max: safeMin };
    }

    return { min: safeMin, max: safeMax };
  }

  function parseAgeRange(rawValue) {
    var text = normalizeText(rawValue);
    var match;

    if (!text) return null;

    match = text.match(/age\s*cluster\s*([1-5])/);
    if (match && AGE_CLUSTER_RANGES[match[1]]) {
      return AGE_CLUSTER_RANGES[match[1]];
    }

    match = text.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*(?:years?|yrs?|y)\b/);
    if (match) return normalizeRange(yearsToMonths(match[1]), yearsToMonths(match[2]));

    match = text.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*(?:months?|mos?|mo|m)\b/);
    if (match) return normalizeRange(Number.parseFloat(match[1]), Number.parseFloat(match[2]));

    match = text.match(/(\d+(?:\.\d+)?)\s*\+\s*(?:years?|yrs?|y)\b/);
    if (match) return normalizeRange(yearsToMonths(match[1]), DEFAULT_MAX_AGE_MONTHS);

    match = text.match(/(\d+(?:\.\d+)?)\s*\+\s*(?:months?|mos?|mo|m)\b/);
    if (match) return normalizeRange(Number.parseFloat(match[1]), DEFAULT_MAX_AGE_MONTHS);

    match = text.match(/\bnewborn\b/);
    if (match) return { min: 0, max: 3 };

    return null;
  }

  function textHasAlias(text, alias) {
    var normalizedText = ' ' + normalizeText(text).replace(/-/g, ' ') + ' ';
    var normalizedAlias = ' ' + normalizeText(alias).replace(/-/g, ' ') + ' ';
    return normalizedText.indexOf(normalizedAlias) !== -1;
  }

  function getSkillSet(rawValue) {
    var text = normalizeText(rawValue);
    var skills = new Set();

    Object.keys(SKILL_ALIASES).forEach(function (skill) {
      if (skill === 'all') return;

      var aliases = SKILL_ALIASES[skill];
      for (var index = 0; index < aliases.length; index += 1) {
        if (textHasAlias(text, aliases[index])) {
          skills.add(skill);
          break;
        }
      }
    });

    return skills;
  }

  function getProductAgeRange(product) {
    if (product.ageRange) return product.ageRange;
    return parseAgeRange([product.ageText, product.tagsText, product.title].join(' '));
  }

  function getProductSkills(product) {
    if (product.skills) return product.skills;
    return getSkillSet([product.skillText, product.tagsText, product.title].join(' '));
  }

  function productMatches(product, filters) {
    var activeAge = filters && Number.isFinite(filters.age) ? filters.age : null;
    var activeFocus = filters && filters.focus ? filters.focus : 'all';
    var ageRange = getProductAgeRange(product);
    var skills = getProductSkills(product);

    if (activeAge !== null && (!ageRange || activeAge < ageRange.min || activeAge > ageRange.max)) {
      return false;
    }

    if (activeFocus && activeFocus !== 'all' && !skills.has(activeFocus)) {
      return false;
    }

    return true;
  }

  function formatAge(months) {
    if (!Number.isFinite(months)) return 'Any age';
    if (months === 0) return '0 months';
    if (months < 12) return months + (months === 1 ? ' month' : ' months');

    var years = Math.floor(months / 12);
    var remainingMonths = months % 12;
    var label = years + (years === 1 ? ' year' : ' years');

    if (remainingMonths > 0) {
      label += ' ' + remainingMonths + (remainingMonths === 1 ? ' month' : ' months');
    }

    return label;
  }

  function formatStage(months) {
    if (!Number.isFinite(months)) return 'Choose an age to begin';
    if (months < 3) return 'Newborn stage';
    if (months < 6) return '3-6 month stage';
    if (months < 12) return '6-12 month stage';
    if (months < 24) return '1-2 year stage';
    return '2-5 year stage';
  }

  function readProductElement(element) {
    return {
      element: element,
      title: element.getAttribute('data-title') || '',
      ageText: element.getAttribute('data-age-text') || '',
      skillText: element.getAttribute('data-skill-text') || '',
      tagsText: element.getAttribute('data-tags-text') || '',
      price: Number.parseFloat(element.getAttribute('data-price')) || 0,
    };
  }

  function setUrlState(state) {
    if (typeof window === 'undefined' || !window.history || !window.location || typeof URLSearchParams === 'undefined') {
      return;
    }

    var params = new URLSearchParams(window.location.search);

    if (Number.isFinite(state.age)) {
      params.set('baby_age', String(state.age));
    } else {
      params.delete('baby_age');
    }

    if (state.focus && state.focus !== 'all') {
      params.set('focus', state.focus);
    } else {
      params.delete('focus');
    }

    var query = params.toString();
    var nextUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    window.history.replaceState(window.history.state || {}, '', nextUrl);
  }

  function getInitialUrlState() {
    if (typeof window === 'undefined' || !window.location || typeof URLSearchParams === 'undefined') {
      return {};
    }

    var params = new URLSearchParams(window.location.search);
    var age = Number.parseInt(params.get('baby_age'), 10);
    var focus = params.get('focus');
    var state = {};

    if (Number.isFinite(age)) state.age = Math.max(0, Math.min(DEFAULT_MAX_AGE_MONTHS, age));
    if (focus && SKILL_ALIASES[focus]) state.focus = focus;

    return state;
  }

  function EnhancedCollection(root) {
    this.root = root || document;
    this.filterPanel = this.root.querySelector('[data-bt-filter-panel]');
    this.products = [];
    this.state = {
      age: null,
      focus: 'all',
    };
    this.pageSize = 16;
    this.currentPage = 1;

    this.refreshProducts();
    this.initializePagination();
    this.initializeFilters();
    this.initializeProductObserver();
    this.initializeSort();
    this.initializeQuickAdd();
    this.initializeFAQ();
    this.applyFilters(false);
  }

  EnhancedCollection.prototype.initializePagination = function () {
    this.pagination = this.root.querySelector('[data-bt-pagination]');
    if (!this.pagination) return;

    var pageSizeAttr = this.pagination.getAttribute('data-bt-page-size');
    var parsed = Number.parseInt(pageSizeAttr, 10);
    if (Number.isFinite(parsed) && parsed > 0) this.pageSize = parsed;

    var totalAttr = this.pagination.getAttribute('data-bt-total-pages');
    var totalParsed = Number.parseInt(totalAttr, 10);
    this.totalPages = Number.isFinite(totalParsed) ? totalParsed : 1;

    this.collectionHandle = this.pagination.getAttribute('data-bt-collection') || '';
    this.sectionId = this.pagination.getAttribute('data-bt-section') || '';

    this.fetchedPages = new Set([1]);
    this.isFetching = false;

    this.loader = this.pagination.querySelector('[data-bt-loader]');
    this.loadMoreButton = this.pagination.querySelector('[data-bt-load-more]');
    this.paginationStatus = this.pagination.querySelector('[data-bt-pagination-status]');

    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener(
        'click',
        function () {
          this.currentPage += 1;
          this.applyFilters(false);
        }.bind(this)
      );
    }
  };

  EnhancedCollection.prototype.fetchPage = function (pageNum) {
    if (this.fetchedPages.has(pageNum) || !this.collectionHandle || !this.sectionId) {
      return Promise.resolve();
    }
    this.fetchedPages.add(pageNum);

    var url =
      '/collections/' +
      this.collectionHandle +
      '?section_id=' +
      this.sectionId +
      '&page=' +
      pageNum;

    var self = this;
    return fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newCards = doc.querySelectorAll('[data-bt-product-card]');
        var grid = self.root.querySelector('#product-grid');
        if (!grid || !newCards.length) return;

        Array.prototype.forEach.call(newCards, function (card) {
          var li = card.closest('li') || card;
          li.setAttribute('data-bt-injected-page', String(pageNum));
          grid.appendChild(li);
        });

        self.refreshProducts();
      })
      .catch(function () {
        self.fetchedPages.delete(pageNum);
      });
  };

  EnhancedCollection.prototype.runWithPageFetch = function (syncUrl) {
    var self = this;
    var state = this.state;
    var isPristine = !Number.isFinite(state.age) && state.focus === 'all';

    if (isPristine || this.totalPages <= 1) {
      this.applyFilters(syncUrl);
      return;
    }

    var pagesToFetch = [];
    for (var p = 2; p <= this.totalPages; p++) {
      if (!this.fetchedPages.has(p)) pagesToFetch.push(p);
    }

    if (pagesToFetch.length === 0) {
      this.applyFilters(syncUrl);
      return;
    }

    if (this.loader) this.loader.hidden = false;
    if (this.pagination) this.pagination.hidden = false;

    Promise.all(pagesToFetch.map(function (p) { return self.fetchPage(p); })).then(function () {
      if (self.loader) self.loader.hidden = true;
      self.applyFilters(syncUrl);
    });
  };

  EnhancedCollection.prototype.refreshProducts = function () {
    this.products = Array.prototype.map.call(this.root.querySelectorAll('[data-bt-product-card]'), readProductElement);
  };

  EnhancedCollection.prototype.initializeProductObserver = function () {
    var container = this.root.querySelector('#ProductGridContainer');

    if (!container || typeof MutationObserver === 'undefined') return;

    this.productObserver = new MutationObserver(
      function () {
        this.refreshProducts();
        this.applyFilters(false);
      }.bind(this)
    );

    this.productObserver.observe(container, { childList: true, subtree: true });
  };

  EnhancedCollection.prototype.initializeFilters = function () {
    var initialState = getInitialUrlState();

    if (!this.filterPanel) return;

    this.ageSlider = this.filterPanel.querySelector('[data-bt-age-slider]');
    this.ageOutput = this.filterPanel.querySelector('[data-bt-age-output]');
    this.stageOutput = this.filterPanel.querySelector('[data-bt-stage-output]');
    this.focusInputs = Array.prototype.slice.call(this.filterPanel.querySelectorAll('[data-bt-focus-input]'));
    this.countElement = this.filterPanel.querySelector('[data-bt-filter-count]');
    this.emptyElement = this.filterPanel.querySelector('[data-bt-filter-empty]');
    this.resetButton = this.filterPanel.querySelector('[data-bt-filter-reset]');

    if (Number.isFinite(initialState.age)) {
      this.state.age = initialState.age;
      if (this.ageSlider) this.ageSlider.value = String(initialState.age);
      this.filterPanel.classList.remove('is-pristine');
    }

    if (initialState.focus) {
      this.state.focus = initialState.focus;
      this.focusInputs.forEach(function (input) {
        input.checked = input.value === initialState.focus;
      });
    }

    if (this.ageSlider) {
      this.updateAgeControl();
      var applyDebounceTimer = null;
      this.ageSlider.addEventListener(
        'input',
        function (event) {
          this.state.age = Number.parseInt(event.target.value, 10);
          this.currentPage = 1;
          this.filterPanel.classList.remove('is-pristine');
          this.updateAgeControl(true);
          clearTimeout(applyDebounceTimer);
          applyDebounceTimer = setTimeout(
            function () { this.runWithPageFetch(true); }.bind(this),
            220
          );
        }.bind(this)
      );
    }

    this.focusInputs.forEach(
      function (input) {
        input.addEventListener(
          'change',
          function (event) {
            if (!event.target.checked) return;
            this.state.focus = event.target.value || 'all';
            this.currentPage = 1;
            this.runWithPageFetch(true);
          }.bind(this)
        );
      }.bind(this)
    );

    if (this.resetButton) {
      this.resetButton.addEventListener(
        'click',
        function () {
          this.resetFilters();
        }.bind(this)
      );
    }
  };

  EnhancedCollection.prototype.updateAgeControl = function (bump) {
    if (!this.ageSlider) return;

    var value = Number.parseInt(this.ageSlider.value, 10);
    var min = Number.parseInt(this.ageSlider.min || '0', 10);
    var max = Number.parseInt(this.ageSlider.max || String(DEFAULT_MAX_AGE_MONTHS), 10);
    var hasAge = Number.isFinite(this.state.age);
    var percentage = hasAge ? ((value - min) * 100) / (max - min) : 0;

    this.ageSlider.style.setProperty('--bt-age-slider-progress', percentage + '%');

    if (this.ageOutput) {
      this.ageOutput.textContent = hasAge ? formatAge(value) : 'Pick an age';
      this.ageOutput.setAttribute('data-state', hasAge ? 'set' : 'empty');

      if (bump && hasAge) {
        this.ageOutput.classList.remove('is-bumped');
        // Force reflow so the animation restarts on rapid changes.
        // eslint-disable-next-line no-unused-expressions
        this.ageOutput.offsetWidth;
        this.ageOutput.classList.add('is-bumped');
      }
    }

    if (this.stageOutput) {
      this.stageOutput.textContent = hasAge ? formatStage(value) : 'Slide to your baby’s age';
    }
  };

  EnhancedCollection.prototype.resetFilters = function () {
    this.state.age = null;
    this.state.focus = 'all';
    this.currentPage = 1;

    if (this.ageSlider) {
      this.ageSlider.value = '0';
    }

    this.focusInputs.forEach(function (input) {
      input.checked = input.value === 'all';
    });

    if (this.filterPanel) {
      this.filterPanel.classList.add('is-pristine');
    }

    if (this.ageOutput) {
      this.ageOutput.classList.remove('is-bumped');
    }

    this.updateAgeControl();
    this.applyFilters(true);
  };

  EnhancedCollection.prototype.applyFilters = function (syncUrl) {
    var state = this.state;
    var isPristine = !Number.isFinite(state.age) && state.focus === 'all';
    var paginationLimit = this.pageSize * this.currentPage;
    var matchingTotal = 0;
    var paginationVisible = 0;

    this.products.forEach(function (product) {
      var matches = productMatches(product, state);
      var hiddenByPagination = false;

      if (matches) {
        matchingTotal += 1;
        if (isPristine) {
          if (paginationVisible >= paginationLimit) {
            hiddenByPagination = true;
          } else {
            paginationVisible += 1;
          }
        }
      }

      product.element.classList.toggle('bt-filter-hidden', !matches);
      product.element.classList.toggle('bt-paginated-hidden', hiddenByPagination);
      product.element.setAttribute('aria-hidden', !matches || hiddenByPagination ? 'true' : 'false');
    });

    this.updateProductCount(matchingTotal);
    this.updatePagination(isPristine, matchingTotal, paginationVisible);
    this.toggleShopifyPagination(isPristine);

    if (this.emptyElement) {
      this.emptyElement.hidden = matchingTotal > 0;
    }

    if (syncUrl) {
      setUrlState(state);
    }
  };

  EnhancedCollection.prototype.toggleShopifyPagination = function (isPristine) {
    var shopifyPagination = this.root.querySelector('.pagination');
    if (!shopifyPagination) return;
    shopifyPagination.style.display = isPristine ? '' : 'none';
  };

  EnhancedCollection.prototype.updatePagination = function (isPristine, total, shown) {
    if (!this.pagination) return;

    if (!isPristine || total <= this.pageSize) {
      this.pagination.hidden = true;
      return;
    }

    this.pagination.hidden = false;
    if (this.loader) this.loader.hidden = true;

    if (this.paginationStatus) {
      this.paginationStatus.textContent = 'Showing ' + shown + ' of ' + total + ' toys';
    }

    if (this.loadMoreButton) {
      var done = shown >= total;
      this.loadMoreButton.hidden = done;
      this.loadMoreButton.disabled = done;
    }
  };

  EnhancedCollection.prototype.updateProductCount = function (visibleCount) {
    var countLabel = visibleCount + (visibleCount === 1 ? ' toy' : ' toys');
    var guidedCountLabel = Number.isFinite(this.state.age)
      ? 'Best fits: ' + countLabel
      : countLabel + ' available';

    if (this.countElement) {
      this.countElement.textContent = guidedCountLabel;
    }

    var localCount = this.root.querySelector('.product-count span');
    if (localCount) {
      localCount.textContent = countLabel;
    }

    var dawnCount = this.root.querySelector('#ProductCountDesktop, #ProductCount');
    if (dawnCount) {
      dawnCount.textContent = countLabel;
    }
  };

  EnhancedCollection.prototype.initializeSort = function () {
    var sortSelect = this.root.querySelector('.sort-select');

    if (sortSelect) {
      sortSelect.addEventListener(
        'change',
        function (event) {
          this.sortProducts(event.target.value);
        }.bind(this)
      );
    }
  };

  EnhancedCollection.prototype.sortProducts = function (sortBy) {
    var grid = this.root.querySelector('#product-grid');
    var products;

    if (!grid) return;

    products = Array.prototype.slice.call(grid.children);

    products.sort(
      function (a, b) {
        switch (sortBy) {
          case 'price-ascending':
            return this.getProductPrice(a) - this.getProductPrice(b);
          case 'price-descending':
            return this.getProductPrice(b) - this.getProductPrice(a);
          case 'title-ascending':
            return this.getProductTitle(a).localeCompare(this.getProductTitle(b));
          case 'title-descending':
            return this.getProductTitle(b).localeCompare(this.getProductTitle(a));
          default:
            return 0;
        }
      }.bind(this)
    );

    products.forEach(function (product) {
      grid.appendChild(product);
    });
  };

  EnhancedCollection.prototype.getProductPrice = function (productElement) {
    var dataPrice = Number.parseFloat(productElement.getAttribute('data-price'));
    var priceElement;
    var price;

    if (Number.isFinite(dataPrice)) return dataPrice;

    priceElement = productElement.querySelector('.price-current, .price-item--regular, .price-item--sale');
    if (!priceElement) return 0;

    price = priceElement.textContent.replace(/[^0-9.]/g, '');
    return Number.parseFloat(price) || 0;
  };

  EnhancedCollection.prototype.getProductTitle = function (productElement) {
    var dataTitle = productElement.getAttribute('data-title');
    var titleElement;

    if (dataTitle) return dataTitle;

    titleElement = productElement.querySelector('.product-card-title a, .card__heading a');
    return titleElement ? titleElement.textContent.trim() : '';
  };

  EnhancedCollection.prototype.initializeQuickAdd = function () {
    var quickAddButtons = this.root.querySelectorAll('.btn-quick-add');

    Array.prototype.forEach.call(
      quickAddButtons,
      function (button) {
        button.addEventListener(
          'click',
          function (event) {
            event.preventDefault();
            this.quickAddToCart(button.dataset.variantId || button.dataset.productId, button);
          }.bind(this)
        );
      }.bind(this)
    );
  };

  EnhancedCollection.prototype.quickAddToCart = async function (variantId, button) {
    var originalText = button.textContent;
    var response;
    var data;
    var formData;

    if (!variantId) {
      this.onQuickAddError(button);
      return;
    }

    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        data = await response.json();
        this.onQuickAddSuccess(button, data);
      } else {
        this.onQuickAddError(button);
      }
    } catch (error) {
      console.error('Quick add error:', error);
      this.onQuickAddError(button);
    } finally {
      setTimeout(function () {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('success', 'error');
      }, 2000);
    }
  };

  EnhancedCollection.prototype.onQuickAddSuccess = function (button) {
    var cartDrawer;

    button.textContent = 'Added';
    button.classList.add('success');
    this.updateCartCount();
    this.showNotification('Product added to cart', 'success');

    cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  };

  EnhancedCollection.prototype.onQuickAddError = function (button) {
    button.textContent = 'Error';
    button.classList.add('error');
    this.showNotification('Failed to add product. Please try again.', 'error');
  };

  EnhancedCollection.prototype.updateCartCount = async function () {
    var response;
    var cart;
    var cartCount;

    try {
      response = await fetch(window.Shopify.routes.root + 'cart.js');
      cart = await response.json();
      cartCount = document.querySelector('.cart-count-bubble');

      if (cartCount) {
        cartCount.textContent = cart.item_count;
        cartCount.classList.remove('hidden');
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  };

  EnhancedCollection.prototype.showNotification = function (message, type) {
    var notification = document.createElement('div');

    notification.className = 'collection-notification notification--' + (type || 'info');
    notification.innerHTML =
      '<div class="notification-content">' +
      '<span class="notification-message"></span>' +
      '<button class="notification-close" type="button" aria-label="Close">&times;</button>' +
      '</div>';

    notification.querySelector('.notification-message').textContent = message;
    document.body.appendChild(notification);

    notification.querySelector('.notification-close').addEventListener('click', function () {
      notification.remove();
    });

    setTimeout(function () {
      notification.classList.add('visible');
    }, 10);

    setTimeout(function () {
      notification.classList.remove('visible');
      setTimeout(function () {
        notification.remove();
      }, 300);
    }, 4000);
  };

  EnhancedCollection.prototype.initializeFAQ = function () {
    var faqQuestions = this.root.querySelectorAll('.faq-question');

    Array.prototype.forEach.call(faqQuestions, function (question) {
      question.addEventListener('click', function () {
        var faqItem = question.closest('.faq-item');
        var answer = faqItem ? faqItem.querySelector('.faq-answer') : null;

        if (!faqItem || !answer) return;

        faqItem.classList.toggle('active');
        answer.style.maxHeight = faqItem.classList.contains('active') ? answer.scrollHeight + 'px' : '0';
      });
    });
  };

  if (typeof window !== 'undefined') {
    window.BrainyTotsCollectionFilters = {
      parseAgeRange: parseAgeRange,
      getSkillSet: getSkillSet,
      productMatches: productMatches,
      formatAge: formatAge,
      formatStage: formatStage,
    };
    window.EnhancedCollection = EnhancedCollection;
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', function () {
      var roots = document.querySelectorAll('.collection-enhanced, .section[id*="ProductGrid"], .section-collection-product-grid');

      if (roots.length === 0) {
        roots = [document];
      }

      Array.prototype.forEach.call(roots, function (root) {
        if (root.querySelector('[data-bt-filter-panel], .product-card-enhanced')) {
          new EnhancedCollection(root);
        }
      });
    });
  }
})();
